/**
 * BAB MTD Report Table — month columns, each cell = YTD(m) - YTD(m-1).
 * Inline-Tailwind port of bab-process-app-v2's BabMtdReportTable.
 */
import { useState, useCallback, useMemo } from 'react';
import { ChevronRight, ChevronDown, MessageSquare, HelpCircle } from 'lucide-react';
import type {
    BabLineDefinition, BabMtdMonth, AmountScale, SignMode, BabCommentCount, BabQueryCount,
} from '../../types/babReport';
import type { BuTheme } from '../../utils/buTheme';
import { formatScaled, applySign } from '../../utils/babFormat';

/** `lineCode|year-month` — the same key the page buckets comments and queries under. */
export function mtdCellKey(lineCode: string, year: number, month: number): string {
    return `${lineCode}|${year}-${month}`;
}

const EMPTY_CELL_COMMENTS: BabCommentCount = { total: 0, line: 0, account: 0, booking: 0 };
const EMPTY_CELL_QUERIES: BabQueryCount = { total: 0, open: 0, overdue: 0 };

function monthHeader(year: number, month: number): string {
    return `${year}-${String(month).padStart(2, '0')}`;
}

function getChildLineCodes(parent: BabLineDefinition, all: BabLineDefinition[]): Set<string> {
    const children = new Set<string>();
    if (parent.operator === 'Range' && parent.calc1 && parent.calc2) {
        const c1 = all.find(l => l.lineCode === parent.calc1);
        const c2 = all.find(l => l.lineCode === parent.calc2);
        if (!c1 || !c2) return children;
        for (const line of all) {
            if (line.lineOrder >= c1.lineOrder && line.lineOrder <= c2.lineOrder) children.add(line.lineCode);
        }
    }
    return children;
}

function getExpandableLines(all: BabLineDefinition[]): Set<string> {
    const expandable = new Set<string>();
    for (const line of all) {
        if (line.operator === 'Range' && line.calc1 && line.calc2) {
            if (getChildLineCodes(line, all).size > 0) expandable.add(line.lineCode);
        }
    }
    return expandable;
}

interface MtdRowData {
    lineDef: BabLineDefinition;
    monthValues: Map<string, number>;
}

interface RowProps {
    row: MtdRowData;
    months: BabMtdMonth[];
    scale: AmountScale;
    signMode: SignMode;
    isExpandable: boolean;
    isExpanded: boolean;
    onToggleExpand: (lineCode: string) => void;
    theme?: BuTheme;
    onCellClick?: (lineCode: string, year: number, month: number) => void;
    /** Keyed by `mtdCellKey`. A cell with notes shows a marker; every cell offers to add one. */
    commentCounts?: Record<string, BabCommentCount>;
    onCommentClick?: (lineCode: string, year: number, month: number) => void;
    queryCounts?: Record<string, BabQueryCount>;
    onQueryClick?: (lineCode: string, year: number, month: number) => void;
}

function MtdRow({
    row, months, scale, signMode, isExpandable, isExpanded, onToggleExpand, theme, onCellClick,
    commentCounts, queryCounts, onCommentClick, onQueryClick,
}: RowProps) {
    const { lineDef } = row;
    const isClickable = lineDef.operator === 'Sum' && !!onCellClick;
    const isKpiAccent = lineDef.isRunningTotal && !!theme;

    const rowClasses = [
        !isKpiAccent && lineDef.isRunningTotal ? 'bg-gray-100 font-semibold' : '',
        !isKpiAccent && lineDef.isSubTotal ? 'font-semibold bg-gray-50' : '',
        isKpiAccent ? 'font-bold' : '',
        isExpandable ? 'cursor-pointer' : '',
    ].filter(Boolean).join(' ');

    const kpiRowStyle = isKpiAccent
        ? { backgroundColor: theme!.primary, color: theme!.textOnAccent === 'text-gray-900' ? '#111827' : '#ffffff' }
        : undefined;

    const indentPx = (lineDef.textIndent * 16) + 24;
    const chevronWidth = 20;
    const firstCellStyle: React.CSSProperties = {
        paddingLeft: `${isExpandable ? indentPx - chevronWidth : indentPx}px`,
        ...(kpiRowStyle ? { color: kpiRowStyle.color } : {}),
    };

    return (
        <tr className={rowClasses} style={kpiRowStyle} onDoubleClick={isExpandable ? () => onToggleExpand(lineDef.lineCode) : undefined}>
            <td className="px-3 py-1.5 font-medium select-none whitespace-nowrap sticky left-0 bg-inherit z-10" style={firstCellStyle}>
                {isExpandable && (
                    <span className="inline-flex items-center mr-0.5 align-middle" style={{ width: `${chevronWidth}px`, color: isKpiAccent ? 'rgba(255,255,255,0.7)' : undefined }}>
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                )}
                {lineDef.lineName}
            </td>
            {months.map(m => {
                const value = row.monthValues.get(`${m.year}-${m.month}`) ?? 0;
                // Red marks a NATIVE negative — an amount that runs against its line's own
                // direction. In `signed` mode every cost cell prints negative, which says
                // nothing, so the colour must keep looking at the unflipped value.
                const cellClass = isKpiAccent ? '' : value < 0 ? 'text-red-600' : '';
                const key = mtdCellKey(lineDef.lineCode, m.year, m.month);
                const cmt = commentCounts?.[key] ?? EMPTY_CELL_COMMENTS;
                const qry = queryCounts?.[key] ?? EMPTY_CELL_QUERIES;
                const iconTint = isKpiAccent ? { color: kpiRowStyle!.color } : undefined;
                return (
                    <td
                        key={`${m.year}-${m.month}`}
                        className={`group relative px-3 py-1.5 text-right tabular-nums ${cellClass} ${isClickable ? 'cursor-pointer hover:bg-black/10' : ''}`}
                        style={isKpiAccent ? { color: kpiRowStyle!.color } : undefined}
                        onClick={isClickable ? () => onCellClick!(lineDef.lineCode, m.year, m.month) : undefined}
                    >
                        {/* Notes on THIS month. `stopPropagation` because the cell itself is
                            the drill-down — without it every note click also opens the matrix.
                            A cell that already has notes shows its marker permanently; an empty
                            one only reveals the icon on hover, so the grid stays a grid. */}
                        {onCommentClick && (
                            <span
                                className="absolute left-1 top-0.5 flex items-center gap-0.5"
                                style={iconTint}
                            >
                                <button
                                    type="button"
                                    onClick={e => { e.stopPropagation(); onCommentClick(lineDef.lineCode, m.year, m.month); }}
                                    title={cmt.total > 0
                                        ? `${cmt.total} comment${cmt.total === 1 ? '' : 's'} on ${monthHeader(m.year, m.month)}`
                                        : `Comment on ${monthHeader(m.year, m.month)}`}
                                    className={`rounded p-0.5 hover:bg-black/10 ${
                                        cmt.total > 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'}`}
                                >
                                    <MessageSquare size={11} className={cmt.total > 0 && !isKpiAccent ? 'text-blue-600' : ''} />
                                </button>
                                {cmt.total > 0 && (
                                    <span className={`text-[9px] font-semibold leading-none ${isKpiAccent ? '' : 'text-blue-600'}`}>
                                        {cmt.total}
                                    </span>
                                )}
                                {onQueryClick && (
                                    <button
                                        type="button"
                                        onClick={e => { e.stopPropagation(); onQueryClick(lineDef.lineCode, m.year, m.month); }}
                                        title={qry.total > 0
                                            ? `${qry.total} quer${qry.total === 1 ? 'y' : 'ies'} on ${monthHeader(m.year, m.month)}${qry.overdue > 0 ? `, ${qry.overdue} overdue` : ''}`
                                            : `Ask about ${monthHeader(m.year, m.month)}`}
                                        className={`rounded p-0.5 hover:bg-black/10 ${
                                            qry.total > 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'}`}
                                    >
                                        <HelpCircle
                                            size={11}
                                            className={qry.total > 0 && !isKpiAccent
                                                ? (qry.overdue > 0 ? 'text-red-600' : qry.open > 0 ? 'text-amber-600' : 'text-gray-500')
                                                : ''}
                                        />
                                    </button>
                                )}
                            </span>
                        )}
                        {formatScaled(applySign(value, lineDef.signage, signMode), scale)}
                    </td>
                );
            })}
        </tr>
    );
}

interface BabMtdReportTableProps {
    months: BabMtdMonth[];
    lineDefinitions: BabLineDefinition[];
    maxViewLevel?: number;
    scale?: AmountScale;
    signMode?: SignMode;
    theme?: BuTheme;
    onCellClick?: (lineCode: string, year: number, month: number) => void;
    /** Per (line, month) notes — keyed by `mtdCellKey`. */
    commentCounts?: Record<string, BabCommentCount>;
    onCommentClick?: (lineCode: string, year: number, month: number) => void;
    queryCounts?: Record<string, BabQueryCount>;
    onQueryClick?: (lineCode: string, year: number, month: number) => void;
}

export function BabMtdReportTable({
    months, lineDefinitions, maxViewLevel = 0, scale = 'thousands', signMode = 'bab', theme,
    onCellClick, commentCounts, onCommentClick, queryCounts, onQueryClick,
}: BabMtdReportTableProps) {
    const [expandedLines, setExpandedLines] = useState<Set<string>>(new Set());

    const toggleExpand = useCallback((lineCode: string) => {
        setExpandedLines(prev => {
            const next = new Set(prev);
            if (next.has(lineCode)) next.delete(lineCode); else next.add(lineCode);
            return next;
        });
    }, []);

    const expandableLines = useMemo(() => getExpandableLines(lineDefinitions), [lineDefinitions]);

    // Newest month first (left-most column)
    const sortedMonths = useMemo(
        () => [...months].sort((a, b) => (b.year - a.year) || (b.month - a.month)),
        [months]
    );

    const childToParents = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const line of lineDefinitions) {
            if (expandableLines.has(line.lineCode)) {
                for (const child of getChildLineCodes(line, lineDefinitions)) {
                    const parents = map.get(child) || [];
                    parents.push(line.lineCode);
                    map.set(child, parents);
                }
            }
        }
        return map;
    }, [lineDefinitions, expandableLines]);

    const rows: MtdRowData[] = useMemo(() => {
        return lineDefinitions.map(lineDef => {
            const monthValues = new Map<string, number>();
            for (const m of months) {
                const lineVal = m.lines.find(l => l.lineCode === lineDef.lineCode);
                monthValues.set(`${m.year}-${m.month}`, lineVal?.mtdAmount ?? 0);
            }
            return { lineDef, monthValues };
        });
    }, [lineDefinitions, months]);

    const visibleRows = useMemo(() => {
        return rows.filter(row => {
            const vl = row.lineDef.viewLevel ?? 0;
            if (vl >= 9) return false;
            const passesViewLevel = maxViewLevel === 0 || vl <= maxViewLevel;
            const parents = childToParents.get(row.lineDef.lineCode);
            if (parents && parents.length > 0 && !passesViewLevel) {
                return parents.every(p => expandedLines.has(p));
            }
            return passesViewLevel;
        });
    }, [rows, maxViewLevel, expandedLines, childToParents]);

    if (!months || months.length === 0) {
        return <div className="text-center py-12 text-gray-500">No MTD data available for the selected period and filters.</div>;
    }

    const th = 'sticky top-0 z-20 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500';
    const thCorner = 'sticky top-0 left-0 z-30 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500';

    return (
        <>
        {/* Bounded on purpose — see BabReportTable: a sticky <th> pins to its nearest
            scrolling ancestor, so that ancestor must be able to scroll vertically.
            Horizontal scrolling (the month columns) keeps working in the same box. */}
        <div className="overflow-auto max-h-[calc(100vh-300px)]">
            <table className="min-w-full text-[15px] border-collapse">
                <thead>
                    <tr className="border-b border-gray-200">
                        <th className={`${thCorner} text-left w-56 whitespace-nowrap`}>Description</th>
                        {sortedMonths.map(m => (
                            <th key={`${m.year}-${m.month}`} className={`${th} text-right whitespace-nowrap`}>{monthHeader(m.year, m.month)}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {visibleRows.map(row => (
                        <MtdRow
                            key={row.lineDef.lineCode}
                            row={row}
                            months={sortedMonths}
                            scale={scale}
                            signMode={signMode}
                            isExpandable={expandableLines.has(row.lineDef.lineCode)}
                            isExpanded={expandedLines.has(row.lineDef.lineCode)}
                            onToggleExpand={toggleExpand}
                            theme={theme}
                            onCellClick={onCellClick}
                            commentCounts={commentCounts}
                            onCommentClick={onCommentClick}
                            queryCounts={queryCounts}
                            onQueryClick={onQueryClick}
                        />
                    ))}
                </tbody>
            </table>
        </div>
        <p className="mt-2 px-3 pb-2 text-xs text-gray-400">
            Each column shows the month-to-date value (YTD minus previous month). Double-click a subtotal row to expand.
            {onCellClick && ' Click a cell of a detail line for its accounts across all months.'}
        </p>
        </>
    );
}
